import React, { createContext, useContext, useState, useCallback } from "react";

// Simplified Chinese (简体). Add more keys here as we translate the rest of the app.
const translations = {
  en: {
    "login.tagline_1": "Your ledger,",
    "login.tagline_2": "digitized & precise.",
    "login.desc": "Track loans, repayments, branch cash movement, collection risk and monthly profitability — all in one control room.",
    "login.platform": "Micro-lending operations platform",
    "login.title": "Sign in",
    "login.subtitle": "Access your branch dashboard.",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.error_generic": "Unable to sign in. Please try again.",
    "nav.dashboard": "Dashboard",
    "nav.group.overview": "Overview",
    "nav.group.lending": "Lending",
    "nav.group.collections": "Collections",
    "nav.group.finance": "Finance & Reports",
    "nav.group.admin": "Data & Admin",
    "nav.group.settings": "Settings",
    "nav.loans": "Loans",
    "nav.customers": "Customers",
    "nav.new-loan": "New Loan",
    "nav.payments": "Payments",
    "nav.ledger": "Branch Ledger",
    "nav.collection": "Collection Priority",
    "nav.bad-debt": "Bad Debt",
    "nav.collectors": "Collector Workbench",
    "nav.handovers": "Cash Handovers",
    "nav.monthly": "Monthly Income",
    "nav.commission": "Manager Commission",
    "nav.director-statement": "Director Statement",
    "nav.interest": "Potential Interest",
    "nav.data-health": "Data Health",
    "nav.deleted-loans": "Deleted Loans",
    "nav.activity": "Activity Log",
    "nav.logins": "Login Records",
    "login_records.title": "Login Records",
    "login_records.subtitle": "Who logged in, when, and from where",
    "login_records.period": "Period",
    "login_records.last_7": "Last 7 days",
    "login_records.last_30": "Last 30 days",
    "login_records.last_90": "Last 90 days",
    "login_records.distinct_users": "People logged in",
    "login_records.total_logins": "Total logins",
    "login_records.when": "When",
    "login_records.user": "User",
    "login_records.role": "Role",
    "login_records.branch": "Branch",
    "login_records.ip": "IP Address",
    "login_records.empty": "No logins recorded for this period yet.",
    "nav.users": "Access Control",
    "nav.account": "Account",
    "sidebar.all_branches": "All Branches",
    "sidebar.hide": "Hide Sidebar",
    "sidebar.logout": "Logout",
    "sidebar.user": "User",
    "common.new_loan": "New Loan",
    "common.period": "Period",
    "common.view_all": "View all",
    "common.view": "View",
    "common.loading": "Loading…",
    "dash.title": "Dashboard",
    "dash.subtitle": "Branch financial overview & key metrics",
    "dash.verify_records": "records need date verification",
    "dash.verify_desc": "Some entries have future dates that look wrong. Please review and correct them in Data Health — this reminder stays until they're fixed.",
    "dash.loan_disbursement": "Loan Disbursement",
    "dash.loans_word": "loans",
    "dash.cash_balance": "Current Cash Balance",
    "dash.live_ledger": "Live ledger balance",
    "dash.income": "Income",
    "dash.interest_collected": "Interest collected",
    "dash.operating_expenses": "Operating Expenses",
    "dash.office_salary": "Office, salary, etc.",
    "dash.net_profit": "Net Profit",
    "dash.np_all": "Cash + outstanding − capital",
    "dash.capital_invested": "Capital Company Invested",
    "dash.capital_sub": "Total Capital In to branch",
    "dash.outstanding": "Outstanding",
    "dash.outstanding_sub": "active loans · paid ≤90d",
    "dash.inactive_title": "Inactive Outstanding (dormant 90+ days)",
    "dash.inactive_desc": "loans with no payment activity in the last 90 days",
    "dash.recent_loans": "Recent Loans",
    "dash.no_loans": "No loans yet.",
    "dash.collection_risk": "Collection Risk",
    "dash.no_risk": "No risk flags. 🎉",
    "col.code": "Code",
    "col.customer": "Customer",
    "col.applied": "Applied",
    "col.outstanding": "Outstanding",
    "col.status": "Status",
    "col.date": "Date",
    "col.agent": "Agent",
    "col.loan_apply": "Loan Apply",
    "col.in_hand": "In Hand",
    "col.formula": "Formula",
    "col.pending_int": "Pending Int.",
    "col.pl": "P/L",
    "col.collection": "Collection",
    "col.type": "Type",
    "col.description": "Description",
    "col.amount": "Amount",
    "col.balance": "Balance",
    "loans.title": "Loans",
    "loans.subtitle": "Loan registry with ledger fields",
    "loans.search_ph": "Search code/customer… (press Enter for customer record)",
    "loans.sort_prefix": "Sort:",
    "loans.agent_all": "Agent: All",
    "loans.desc": "Desc",
    "loans.asc": "Asc",
    "loans.none": "No loans found.",
    "filter.All": "All",
    "filter.Active": "Active",
    "filter.Inactive": "Inactive",
    "filter.Settled": "Settled",
    "filter.Bad Debt": "Bad Debt",
    "sort.recent": "Date",
    "sort.name": "Name (A–Z)",
    "sort.pending": "Pending Interest",
    "sort.outstanding": "Outstanding",
    "sort.pl": "Profit/Loss",
    "ledger.title": "Branch Ledger",
    "ledger.subtitle": "Capital in, expenses & loan disbursements with running balance",
    "ledger.current_balance": "Current Cash Balance",
    "ledger.new_entry": "New Entry",
    "ledger.desc_ph": "e.g. Office rent",
    "ledger.amount_rm": "Amount (RM)",
    "ledger.add_entry": "Add Entry",
    "ledger.timeline": "Cash Movement Timeline",
    "ledger.search_month": "Search this month…",
    "ledger.no_entries": "No entries for this month.",
    "ledger.no_match": "No entries match your search.",
    "ledger.toast_amount": "Enter an amount",
    "ledger.toast_added": "Ledger entry added",
    "ledger.toast_removed": "Entry removed",
    "ledger.auto": "(auto)",
    "ledger.future": "future",
    "ledger.future_note_a": "This ledger has",
    "ledger.future_note_b": "future-dated entries. They are shown but NOT counted in the current balance (the balance is as of today).",
    "type.Capital In": "Capital In",
    "type.Collection In": "Collection In",
    "type.Expense": "Expense",
    "type.New Loan Out": "New Loan Out",
    "coll.title": "Collection Workflow",
    "coll.subtitle": "Follow-ups & due collections",
    "coll.wa_reminders": "WhatsApp Reminders",
    "coll.wa_desc": "Tap the green WhatsApp button on any row to message that customer (opens your WhatsApp with the reminder pre-filled).",
    "coll.send_all": "Send all due now — Production only (not active)",
    "coll.tab.dueToday": "Due Today",
    "coll.tab.dueTomorrow": "Due Tomorrow",
    "coll.tab.overdue": "Overdue",
    "coll.tab.upcoming": "Upcoming",
    "coll.tab.nextMonth": "Next Month",
    "coll.tab.redFlags": "Red Flags",
    "coll.tab.whiteBoard": "White Board",
    "coll.due_day": "Due Day",
    "coll.last_pay": "Last Pay",
    "coll.overdue_col": "Overdue",
    "coll.flags": "Flags",
    "coll.whatsapp": "WhatsApp",
    "coll.action": "Action",
    "coll.take_action": "Take Action",
    "coll.manual": "Manual",
    "coll.promised": "Promised",
    "coll.due_status": "Due",
    "coll.empty_due": "No accounts here. Set a Due Day on active loans to populate this list.",
    "coll.empty_white": "No super bad debt accounts.",
    "coll.empty_red": "No red flags.",
    "coll.nextmonth_note": "Already collected this month, or new loans whose first repayment falls next month.",
    "coll.white_note": "Super bad debt — never paid & over 90 days since disbursement.",
    "coll.super_bad": "Super Bad Debt",
    "coll.inactive_badge": "Inactive",
    "coll.no_phone": "No phone number saved for this customer. Add one on the loan page.",
    "nav.overview": "All-Branch Overview",
    "ov.title": "All-Branch Overview",
    "ov.subtitle": "Cash, active loans & monthly profit side-by-side",
    "ov.total_cash": "Total Cash Balance",
    "ov.total_active": "Total Active Loans",
    "ov.total_outstanding": "Total Outstanding",
    "ov.month_profit": "Month Profit (all branches)",
    "ov.col_branch": "Branch",
    "ov.col_cash": "Cash Balance",
    "ov.col_active": "Active Loans",
    "ov.col_outstanding": "Outstanding",
    "ov.col_month_profit": "Month Profit",
    "ov.all_branches": "All Branches",
    "ov.month_label": "Month",
    "ov.none": "No branches to show.",
    "ov.note": "Cash balance is the all-time running ledger balance. Month Profit = interest collected − operating expenses for the selected month. Outstanding counts active customers (paid within 90 days).",
    "common.loading": "Loading…",
    "common.none_found": "No records found.",
    "cust.title": "Customers",
    "cust.subtitle": "All customers with profit / loss across their loans",
    "cust.stat_customers": "Customers",
    "cust.stat_disbursed": "Total Disbursed",
    "cust.stat_income": "Total Income",
    "cust.stat_outstanding": "Total Outstanding",
    "cust.stat_pl": "Total Profit / Loss",
    "cust.search_ph": "Search name, code, phone or IC…",
    "cust.sort": "Sort",
    "cust.sort_pl": "Profit / Loss",
    "cust.sort_outstanding": "Outstanding",
    "cust.sort_income": "Income",
    "cust.sort_loans": "Loan Count",
    "cust.sort_name": "Name",
    "cust.desc": "Desc",
    "cust.asc": "Asc",
    "cust.col_customer": "Customer",
    "cust.col_code": "Code",
    "cust.col_loans": "Loans",
    "cust.col_disbursed": "Disbursed",
    "cust.col_income": "Income",
    "cust.col_outstanding": "Outstanding",
    "cust.col_pl": "Profit / Loss",
    "cust.active": "active",
    "cust.settled": "settled",
    "cust.none": "No customers found.",
    "nl.title": "New Loan",
    "nl.subtitle": "Enter loan exactly as your physical ledger",
    "nl.create_in_branch": "Create loan in branch *",
    "nl.select_branch": "Select branch…",
    "nl.loan_code": "Loan Code *",
    "nl.date_applied": "Date Applied",
    "nl.loan_type": "Loan Type *",
    "nl.personal": "Personal",
    "nl.personal_desc": "Pays on salary day each month",
    "nl.business": "Business",
    "nl.business_desc": "Pays every N days from apply/last payment",
    "nl.cycle_days": "Payment Cycle (days) *",
    "nl.salary_day": "Salary Day / Collection Due Day (day of month, 1–31)",
    "nl.customer_name": "Customer Name *",
    "nl.full_name": "Full name",
    "nl.phone": "Phone (WhatsApp)",
    "nl.ic": "IC / Passport No.",
    "nl.address": "Address",
    "nl.address_ph": "Customer address",
    "nl.loan_apply": "Loan Apply (RM) *",
    "nl.stamping": "Stamping (RM)",
    "nl.commission": "Commission (RM)",
    "nl.formula": "Repayment Formula",
    "nl.risk_tag": "Risk Tag",
    "nl.initial_status": "Initial Status",
    "nl.notes": "Notes",
    "nl.notes_ph": "Any remarks…",
    "nl.cancel": "Cancel",
    "nl.create_loan": "Create Loan",
    "nl.live_calc": "Live Calculation",
    "nl.in_hand": "In Hand",
    "nl.money_used": "Money Used",
    "nl.calc_formula": "In Hand = Loan − Stamping · Money Used = In Hand + Commission",
    "nl.formula_parser": "Formula Parser",
    "nl.enter_formula": "Enter a formula to see parsed output.",
    "nl.unrecognized": "Unrecognized format. Try the examples shown.",
    "nl.monthly_interest": "Monthly Interest",
    "nl.monthly_principal": "Monthly Principal",
    "nl.settlement": "Settlement",
    "nl.duration": "Duration",
    "nl.months": "months",
    "nl.err_select_branch": "Select a branch for this loan.",
    "nl.err_required": "Code, customer name and loan apply amount are required.",
    "nl.err_create": "Failed to create loan",
    "acct.manual_title": "My User Manual",
    "acct.manual_desc": "Download your bilingual (中文 / English) guide for your role.",
    "acct.manual_download": "Download My Manual (PDF)",
    "acct.manual_all": "Download All Manuals (ZIP)",
    "acct.manual_none": "No manual is available for your account.",
    // Data values (status / risk / stage / ledger)
    "val.Active": "Active", "val.Settled": "Settled", "val.Bad Debt": "Bad Debt",
    "val.White Board": "White Board", "val.Red Flag": "Red Flag", "val.Overdue": "Overdue",
    "val.None": "None", "val.Bankrupt": "Bankrupt", "val.Lost Contact": "Lost Contact",
    "val.not_assigned": "Not Assigned", "val.chasing": "Chasing",
    "val.Expense": "Expense", "val.Capital In": "Capital In", "val.Collection In": "Collection In",
    "val.Loan Out": "Loan Out",
    "val.Inactive": "Inactive", "val.Due Today": "Due Today", "val.Due Tomorrow": "Due Tomorrow", "val.Upcoming": "Upcoming",
    "pay.title": "Payments", "pay.subtitle": "Record repayments against active loans",
    "pay.add": "Add Payment", "pay.loan": "Loan", "pay.select_loan": "Select loan…",
    "pay.search_loan": "Search code or customer…", "pay.no_match": "No loans match.",
    "pay.outstanding": "Outstanding", "pay.date": "Date", "pay.income": "Income",
    "pay.principal": "Principal", "pay.total": "Total Payment", "pay.record": "Record Payment",
    "pay.all": "All Payments", "pay.col_date": "Date", "pay.col_customer": "Customer Name",
    "pay.col_code": "Customer Code", "pay.col_total": "Total", "pay.none": "No payments recorded.",
    "pay.err_select": "Select a loan", "pay.added": "Payment added. Outstanding now",
    "stage.not_assigned": "Not Assigned", "stage.chasing": "Chasing",
    "bd.title": "Bad Debt", "bd.subtitle": "Written-off loans and pending requests",
    "bd.total_written": "Total Written Off", "bd.loans": "Bad Debt Loans", "bd.pending": "Pending Requests",
    "bd.no_action": "No Collector Action", "bd.chasing": "Collector Chasing",
    "bd.col_loan": "Loan", "bd.col_customer": "Customer", "bd.col_branch": "Branch",
    "bd.col_outstanding": "Outstanding", "bd.col_reason": "Reason", "bd.col_requested": "Requested By",
    "bd.col_action": "Action", "bd.col_stage": "Collector Stage",
    "bd.approve": "Approve", "bd.reject": "Reject", "bd.f_all": "All", "bd.f_none": "No Collector",
    "bd.f_chasing": "Chasing", "bd.none": "No bad-debt loans.", "bd.unassign": "Unassign",
    "bd.assign": "Assign Collector", "bd.assign_title": "Assign to Collector",
    "bd.assign_hint": "enter the collector's name.", "bd.collector_ph": "Collector name",
    "bd.cancel": "Cancel", "bd.assign_confirm": "Assign", "bd.loan_word": "Loan",
    "ld.add_payment": "Add Payment", "ld.save": "Save", "ld.cancel": "Cancel",
    "ld.payment_history": "Payment History", "ld.mark_bad_debt": "Mark as Bad Debt",
    "ld.delete_confirm": "Delete this loan, all its payments, and reverse its cash-movement entries (disbursement + collections) in the branch ledger? This cannot be undone.",
    "mi.title": "Monthly Income Statement", "mi.all_years": "All Years", "mi.net_cash": "Net Cash",
    "ds.title": "Director Statement", "ds.subtitle": "Each director's 50% partnership share across all branches",
    "ds.all_profit": "All Directors Profit", "ds.shared_loans": "Shared Loans", "ds.capital_out": "Capital Out",
    "ds.principal_back": "Principal Back", "ds.all_branches": "All Branches",
    "ho.title": "Cash Handovers", "ho.subtitle": "Branch Manager hands collected cash to Director with photo proof",
    "ac.title": "Access Control", "ac.subtitle": "Set who can edit data and who is view-only",
    "acct.settings": "Account Settings", "acct.change_pw": "Change Password",
  },
  zh: {
    "login.tagline_1": "您的账本，",
    "login.tagline_2": "数字化，精准无误。",
    "login.desc": "在一个控制中心追踪贷款、还款、分行现金流动、催收风险与每月盈亏。",
    "login.platform": "小额贷款运营平台",
    "login.title": "登录",
    "login.subtitle": "访问您的分行仪表板。",
    "login.email": "邮箱",
    "login.password": "密码",
    "login.submit": "登录",
    "login.error_generic": "无法登录，请重试。",
    "nav.dashboard": "仪表板",
    "nav.group.overview": "概览",
    "nav.group.lending": "放贷业务",
    "nav.group.collections": "催收管理",
    "nav.group.finance": "财务与报表",
    "nav.group.admin": "数据与管理",
    "nav.group.settings": "设置",
    "nav.loans": "贷款",
    "nav.customers": "客户",
    "nav.new-loan": "新增贷款",
    "nav.payments": "还款",
    "nav.ledger": "分行账本",
    "nav.collection": "催收优先级",
    "nav.bad-debt": "坏账",
    "nav.collectors": "催收员工作台",
    "nav.handovers": "现金交接",
    "nav.monthly": "每月收入",
    "nav.commission": "经理佣金",
    "nav.director-statement": "董事报表",
    "nav.interest": "潜在利息",
    "nav.data-health": "数据健康",
    "nav.deleted-loans": "已删除贷款",
    "nav.activity": "活动日志",
    "nav.logins": "登录记录",
    "login_records.title": "登录记录",
    "login_records.subtitle": "谁在什么时候、从哪里登录",
    "login_records.period": "时间范围",
    "login_records.last_7": "最近 7 天",
    "login_records.last_30": "最近 30 天",
    "login_records.last_90": "最近 90 天",
    "login_records.distinct_users": "登录人数",
    "login_records.total_logins": "登录总次数",
    "login_records.when": "时间",
    "login_records.user": "用户",
    "login_records.role": "角色",
    "login_records.branch": "分行",
    "login_records.ip": "IP 地址",
    "login_records.empty": "此时间范围内暂无登录记录。",
    "nav.users": "权限管理",
    "nav.account": "账户",
    "sidebar.all_branches": "所有分行",
    "sidebar.hide": "隐藏侧栏",
    "sidebar.logout": "登出",
    "sidebar.user": "用户",
    "common.new_loan": "新增贷款",
    "common.period": "时段",
    "common.view_all": "查看全部",
    "common.view": "查看",
    "common.loading": "加载中……",
    "dash.title": "仪表板",
    "dash.subtitle": "分行财务概览与关键指标",
    "dash.verify_records": "条记录需要日期核实",
    "dash.verify_desc": "部分记录的日期是未来日期，看起来有误。请在“数据健康”中检查并更正——此提醒会持续显示直到修正为止。",
    "dash.loan_disbursement": "放款总额",
    "dash.loans_word": "笔贷款",
    "dash.cash_balance": "当前现金余额",
    "dash.live_ledger": "实时账本余额",
    "dash.income": "收入",
    "dash.interest_collected": "已收利息",
    "dash.operating_expenses": "运营支出",
    "dash.office_salary": "办公室、薪资等",
    "dash.net_profit": "净利润",
    "dash.np_all": "现金 + 未收 − 资本",
    "dash.capital_invested": "公司投入资本",
    "dash.capital_sub": "投入分行的资本总额",
    "dash.outstanding": "未收款",
    "dash.outstanding_sub": "笔活跃贷款 · 90天内已还",
    "dash.inactive_title": "呆滞未收款（90天以上无还款）",
    "dash.inactive_desc": "笔贷款在过去90天内无还款记录",
    "dash.recent_loans": "最近贷款",
    "dash.no_loans": "暂无贷款。",
    "dash.collection_risk": "催收风险",
    "dash.no_risk": "没有风险标记。🎉",
    "col.code": "编号",
    "col.customer": "客户",
    "col.applied": "申请日期",
    "col.outstanding": "未收款",
    "col.status": "状态",
    "col.date": "日期",
    "col.agent": "代理",
    "col.loan_apply": "申请金额",
    "col.in_hand": "实收",
    "col.formula": "公式",
    "col.pending_int": "待收利息",
    "col.pl": "盈亏",
    "col.collection": "催收",
    "col.type": "类型",
    "col.description": "描述",
    "col.amount": "金额",
    "col.balance": "余额",
    "loans.title": "贷款",
    "loans.subtitle": "贷款登记与账本字段",
    "loans.search_ph": "搜索编号/客户……（按回车查看客户档案）",
    "loans.sort_prefix": "排序：",
    "loans.agent_all": "代理：全部",
    "loans.desc": "降序",
    "loans.asc": "升序",
    "loans.none": "未找到贷款。",
    "filter.All": "全部",
    "filter.Active": "活跃",
    "filter.Inactive": "呆滞",
    "filter.Settled": "已结清",
    "filter.Bad Debt": "坏账",
    "sort.recent": "日期",
    "sort.name": "姓名 (A–Z)",
    "sort.pending": "待收利息",
    "sort.outstanding": "未收款",
    "sort.pl": "盈亏",
    "ledger.title": "分行账本",
    "ledger.subtitle": "资本注入、支出与放款及累计余额",
    "ledger.current_balance": "当前现金余额",
    "ledger.new_entry": "新增记录",
    "ledger.desc_ph": "例如：办公室租金",
    "ledger.amount_rm": "金额 (RM)",
    "ledger.add_entry": "添加记录",
    "ledger.timeline": "现金流动时间线",
    "ledger.search_month": "搜索本月……",
    "ledger.no_entries": "本月没有记录。",
    "ledger.no_match": "没有符合搜索的记录。",
    "ledger.toast_amount": "请输入金额",
    "ledger.toast_added": "已添加账本记录",
    "ledger.toast_removed": "记录已删除",
    "ledger.auto": "（自动）",
    "ledger.future": "未来",
    "ledger.future_note_a": "此账本有",
    "ledger.future_note_b": "条未来日期的记录。它们会显示，但不计入当前余额（余额为截至今天）。",
    "type.Capital In": "注入资本",
    "type.Collection In": "收款",
    "type.Expense": "支出",
    "type.New Loan Out": "放款",
    "coll.title": "催收工作流",
    "coll.subtitle": "跟进与到期催收",
    "coll.wa_reminders": "WhatsApp 提醒",
    "coll.wa_desc": "点击任意行的绿色 WhatsApp 按钮即可向该客户发送消息（会打开您的 WhatsApp 并预填提醒内容）。",
    "coll.send_all": "立即发送所有到期提醒 — 仅生产环境（未启用）",
    "coll.tab.dueToday": "今日到期",
    "coll.tab.dueTomorrow": "明日到期",
    "coll.tab.overdue": "逾期",
    "coll.tab.upcoming": "即将到期",
    "coll.tab.nextMonth": "下月",
    "coll.tab.redFlags": "红旗警示",
    "coll.tab.whiteBoard": "白板（呆账）",
    "coll.due_day": "到期日",
    "coll.last_pay": "最近还款",
    "coll.overdue_col": "逾期天数",
    "coll.flags": "标记",
    "coll.whatsapp": "WhatsApp",
    "coll.action": "操作",
    "coll.take_action": "处理",
    "coll.manual": "手动",
    "coll.promised": "已承诺",
    "coll.due_status": "待催",
    "coll.empty_due": "暂无账户。请在活跃贷款上设置到期日以显示此列表。",
    "coll.empty_white": "无超级呆账账户。",
    "coll.empty_red": "无红旗警示。",
    "coll.nextmonth_note": "本月已收款，或首次还款在下月的新贷款。",
    "coll.white_note": "超级呆账 — 从未还款且放款已超过90天。",
    "coll.super_bad": "超级呆账",
    "coll.inactive_badge": "呆滞",
    "coll.no_phone": "该客户未保存电话号码，请在贷款页面添加。",
    "nav.overview": "全分行总览",
    "ov.title": "全分行总览",
    "ov.subtitle": "各分行现金、活跃贷款与月利润并排对比",
    "ov.total_cash": "现金余额合计",
    "ov.total_active": "活跃贷款合计",
    "ov.total_outstanding": "未收款合计",
    "ov.month_profit": "本月利润（全部分行）",
    "ov.col_branch": "分行",
    "ov.col_cash": "现金余额",
    "ov.col_active": "活跃贷款",
    "ov.col_outstanding": "未收款",
    "ov.col_month_profit": "月利润",
    "ov.all_branches": "全部分行",
    "ov.month_label": "月份",
    "ov.none": "暂无分行数据。",
    "ov.note": "现金余额为账本的历史累计余额。月利润 = 所选月份的已收利息 − 运营支出。未收款只统计活跃客户（90 天内有还款）。",
    "common.loading": "加载中…",
    "common.none_found": "未找到记录。",
    "cust.title": "客户",
    "cust.subtitle": "所有客户及其名下贷款的盈亏情况",
    "cust.stat_customers": "客户数",
    "cust.stat_disbursed": "累计放款",
    "cust.stat_income": "累计收入",
    "cust.stat_outstanding": "累计未收款",
    "cust.stat_pl": "累计盈亏",
    "cust.search_ph": "搜索姓名、编号、电话或身份证…",
    "cust.sort": "排序",
    "cust.sort_pl": "盈亏",
    "cust.sort_outstanding": "未收款",
    "cust.sort_income": "收入",
    "cust.sort_loans": "贷款笔数",
    "cust.sort_name": "姓名",
    "cust.desc": "降序",
    "cust.asc": "升序",
    "cust.col_customer": "客户",
    "cust.col_code": "编号",
    "cust.col_loans": "贷款",
    "cust.col_disbursed": "放款",
    "cust.col_income": "收入",
    "cust.col_outstanding": "未收款",
    "cust.col_pl": "盈亏",
    "cust.active": "活跃",
    "cust.settled": "已结清",
    "cust.none": "未找到客户。",
    "nl.title": "新建贷款",
    "nl.subtitle": "按你的纸质账本原样录入贷款",
    "nl.create_in_branch": "在该分行创建贷款 *",
    "nl.select_branch": "选择分行…",
    "nl.loan_code": "贷款编号 *",
    "nl.date_applied": "申请日期",
    "nl.loan_type": "贷款类型 *",
    "nl.personal": "个人",
    "nl.personal_desc": "每月发薪日还款",
    "nl.business": "商业",
    "nl.business_desc": "自申请/上次还款起每 N 天还款",
    "nl.cycle_days": "还款周期（天）*",
    "nl.salary_day": "发薪日 / 应收日（每月第几日，1–31）",
    "nl.customer_name": "客户姓名 *",
    "nl.full_name": "全名",
    "nl.phone": "电话（WhatsApp）",
    "nl.ic": "身份证 / 护照号",
    "nl.address": "地址",
    "nl.address_ph": "客户地址",
    "nl.loan_apply": "申请金额（RM）*",
    "nl.stamping": "印花费（RM）",
    "nl.commission": "佣金（RM）",
    "nl.formula": "还款公式",
    "nl.risk_tag": "风险标签",
    "nl.initial_status": "初始状态",
    "nl.notes": "备注",
    "nl.notes_ph": "任何备注…",
    "nl.cancel": "取消",
    "nl.create_loan": "创建贷款",
    "nl.live_calc": "实时计算",
    "nl.in_hand": "实收（In Hand）",
    "nl.money_used": "占用资金（Money Used）",
    "nl.calc_formula": "客户到手 = 申请金额 − 印花费 · 实际投入 = 客户到手 + 佣金",
    "nl.formula_parser": "公式解析",
    "nl.enter_formula": "输入公式即可查看解析结果。",
    "nl.unrecognized": "无法识别的格式，请参考示例。",
    "nl.monthly_interest": "每月利息",
    "nl.monthly_principal": "每月本金",
    "nl.settlement": "结清金额",
    "nl.duration": "期限",
    "nl.months": "个月",
    "nl.err_select_branch": "请为这笔贷款选择分行。",
    "nl.err_required": "编号、客户姓名和申请金额为必填项。",
    "nl.err_create": "创建贷款失败",
    "acct.manual_title": "我的使用手册",
    "acct.manual_desc": "下载与你角色对应的中英双语使用手册。",
    "acct.manual_download": "下载我的手册（PDF）",
    "acct.manual_all": "下载全部手册（ZIP）",
    "acct.manual_none": "你的账号暂无对应手册。",
    "val.Active": "活跃", "val.Settled": "已结清", "val.Bad Debt": "坏账",
    "val.White Board": "白板（久未还）", "val.Red Flag": "红旗（高风险）", "val.Overdue": "逾期",
    "val.None": "无", "val.Bankrupt": "破产", "val.Lost Contact": "失联",
    "val.not_assigned": "未分配", "val.chasing": "催收中",
    "val.Expense": "支出", "val.Capital In": "注资", "val.Collection In": "回款",
    "val.Loan Out": "放款",
    "val.Inactive": "不活跃", "val.Due Today": "今日到期", "val.Due Tomorrow": "明日到期", "val.Upcoming": "即将到期",
    "pay.title": "还款", "pay.subtitle": "为活跃贷款登记还款",
    "pay.add": "登记还款", "pay.loan": "贷款", "pay.select_loan": "选择贷款…",
    "pay.search_loan": "搜索编号或客户…", "pay.no_match": "没有匹配的贷款。",
    "pay.outstanding": "未收款", "pay.date": "日期", "pay.income": "利息收入",
    "pay.principal": "本金", "pay.total": "本次还款合计", "pay.record": "登记还款",
    "pay.all": "全部还款记录", "pay.col_date": "日期", "pay.col_customer": "客户姓名",
    "pay.col_code": "客户编号", "pay.col_total": "合计", "pay.none": "暂无还款记录。",
    "pay.err_select": "请选择一笔贷款", "pay.added": "已登记还款。当前未收款",
    "stage.not_assigned": "未分配", "stage.chasing": "催收中",
    "bd.title": "坏账", "bd.subtitle": "已核销贷款及待审批申请",
    "bd.total_written": "已核销总额", "bd.loans": "坏账贷款", "bd.pending": "待审批申请",
    "bd.no_action": "未分配催收", "bd.chasing": "催收中",
    "bd.col_loan": "贷款", "bd.col_customer": "客户", "bd.col_branch": "分行",
    "bd.col_outstanding": "未收款", "bd.col_reason": "原因", "bd.col_requested": "申请人",
    "bd.col_action": "操作", "bd.col_stage": "催收阶段",
    "bd.approve": "批准", "bd.reject": "驳回", "bd.f_all": "全部", "bd.f_none": "未分配",
    "bd.f_chasing": "催收中", "bd.none": "暂无坏账贷款。", "bd.unassign": "取消分配",
    "bd.assign": "分配催收员", "bd.assign_title": "分配给催收员",
    "bd.assign_hint": "请输入催收员姓名。", "bd.collector_ph": "催收员姓名",
    "bd.cancel": "取消", "bd.assign_confirm": "分配", "bd.loan_word": "贷款",
    "ld.add_payment": "登记还款", "ld.save": "保存", "ld.cancel": "取消",
    "ld.payment_history": "还款记录", "ld.mark_bad_debt": "标记为坏账",
    "ld.delete_confirm": "删除这笔贷款、其所有还款记录，并冲销其在分行账本中的资金往来（放款 + 回款）？此操作不可撤销。",
    "mi.title": "月度收入表", "mi.all_years": "全部年份", "mi.net_cash": "净现金",
    "ds.title": "董事对账单", "ds.subtitle": "各董事在所有分行的 50% 合伙分润",
    "ds.all_profit": "全体董事利润", "ds.shared_loans": "分润贷款", "ds.capital_out": "放出本金",
    "ds.principal_back": "已收回本金", "ds.all_branches": "全部分行",
    "ho.title": "现金交接", "ho.subtitle": "分行经理将收取的现金连同照片凭证交给董事",
    "ac.title": "权限管理", "ac.subtitle": "设置谁可编辑数据、谁为只读",
    "acct.settings": "账户设置", "acct.change_pw": "修改密码",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "en");

  const setLang = useCallback((l) => {
    localStorage.setItem("lang", l);
    setLangState(l);
  }, []);

  const t = useCallback(
    (key) => (translations[lang] && translations[lang][key]) || translations.en[key] || key,
    [lang]
  );

  // Translate a data value (status, risk tag, collector stage, ledger type) from the DB.
  const tv = useCallback(
    (value) => {
      if (value === null || value === undefined || value === "") return value;
      const key = `val.${value}`;
      return (translations[lang] && translations[lang][key]) || value;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tv }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
